import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Eye, EyeOff, Loader2, Pencil, Plus, Trash2, Wine } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useSession } from '@/features/auth/session'
import {
  deleteFicha,
  listFichas,
  publishFicha,
  unpublishFicha,
  type ReferenceFichaDto,
} from '@/features/fichas/api'
import { fichasCopy } from '@/features/fichas/copy'
import type { FichaStatus } from '@escuela/ficha-domain'

type StatusFilter = 'all' | FichaStatus

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function FichasListPage(): ReactNode {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useSession()
  const isAdmin = user?.rol === 'ADMIN'
  // Permiso de publicar: admin siempre; docente solo si un admin se lo otorgo.
  const canPublish = isAdmin || user?.canPublishFichas === true

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [search, setSearch] = useState('')
  const [authorSearch, setAuthorSearch] = useState('')
  const [toDelete, setToDelete] = useState<ReferenceFichaDto | null>(null)
  const [toPublish, setToPublish] = useState<ReferenceFichaDto | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['fichas', 'list', { status: statusFilter }],
    queryFn: () =>
      listFichas(statusFilter === 'all' ? {} : { status: statusFilter }),
  })

  const items = useMemo<ReferenceFichaDto[]>(() => data ?? [], [data])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const a = authorSearch.trim().toLowerCase()
    return items.filter((f) => {
      if (q && !f.vino.toLowerCase().includes(q)) return false
      if (isAdmin && a) {
        const name = `${f.author.nombre} ${f.author.apellido}`.toLowerCase()
        if (!name.includes(a)) return false
      }
      return true
    })
  }, [items, search, authorSearch, isAdmin])

  const publishMutation = useMutation({
    mutationFn: ({ id, publish }: { id: string; publish: boolean }) =>
      publish ? publishFicha(id) : unpublishFicha(id),
    onSuccess: (_res, vars) => {
      toast.success(vars.publish ? fichasCopy.toasts.published : fichasCopy.toasts.unpublished)
      void queryClient.invalidateQueries({ queryKey: ['fichas'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.updateError)
    },
    onSettled: () => setToPublish(null),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFicha(id),
    onSuccess: () => {
      toast.success(fichasCopy.toasts.deleted)
      void queryClient.invalidateQueries({ queryKey: ['fichas'] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : fichasCopy.toasts.deleteError)
    },
    onSettled: () => setToDelete(null),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{fichasCopy.sectionTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? fichasCopy.sectionDescriptionAdmin : fichasCopy.sectionDescriptionDocente}
          </p>
        </div>
        <Button onClick={() => navigate('/dashboard/fichas/nueva')}>
          <Plus className="h-4 w-4" />
          {fichasCopy.newFicha}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="search">{fichasCopy.filters.search}</Label>
          <Input
            id="search"
            placeholder={fichasCopy.filters.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="status">{fichasCopy.filters.status}</Label>
          <Select
            id="status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">{fichasCopy.filters.statusAll}</option>
            <option value="DRAFT">{fichasCopy.status.draft}</option>
            <option value="PUBLISHED">{fichasCopy.status.published}</option>
          </Select>
        </div>
        {isAdmin ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="author">{fichasCopy.filters.author}</Label>
            <Input
              id="author"
              placeholder={fichasCopy.filters.authorPlaceholder}
              value={authorSearch}
              onChange={(e) => setAuthorSearch(e.target.value)}
            />
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{fichasCopy.columns.vino}</TableHead>
              <TableHead>{fichasCopy.columns.varietal}</TableHead>
              <TableHead>{fichasCopy.columns.bodega}</TableHead>
              {isAdmin ? <TableHead>{fichasCopy.columns.autor}</TableHead> : null}
              <TableHead className="w-[120px]">{fichasCopy.columns.status}</TableHead>
              <TableHead className="w-[140px]">{fichasCopy.columns.fecha}</TableHead>
              <TableHead className="w-[160px] text-right">{fichasCopy.columns.acciones}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((f) => {
              const published = f.status === 'PUBLISHED'
              const varietal = f.conclusiones.variedadUvas.join(', ')
              return (
                <TableRow key={f.id}>
                  <TableCell className="font-medium">{f.vino || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{varietal || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {f.conclusiones.productor || '—'}
                  </TableCell>
                  {isAdmin ? (
                    <TableCell className="text-muted-foreground">
                      {`${f.author.nombre} ${f.author.apellido}`.trim() || '—'}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    {published ? (
                      <Badge variant="success">{fichasCopy.status.published}</Badge>
                    ) : (
                      <Badge variant="muted">{fichasCopy.status.draft}</Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(f.updatedAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" asChild>
                        <Link to={`/dashboard/fichas/${f.id}`} aria-label={fichasCopy.actions.edit}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      {canPublish ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={published ? fichasCopy.actions.unpublish : fichasCopy.actions.publish}
                          disabled={publishMutation.isPending}
                          onClick={() => setToPublish(f)}
                        >
                          {published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={fichasCopy.actions.delete}
                        onClick={() => setToDelete(f)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        <div className="border-t p-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : fichasCopy.toasts.loadError}
            </p>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Wine className="h-8 w-8" />}
              title={fichasCopy.empty.title}
              description={fichasCopy.empty.description}
              action={
                <Button onClick={() => navigate('/dashboard/fichas/nueva')}>
                  <Plus className="h-4 w-4" />
                  {fichasCopy.newFicha}
                </Button>
              }
            />
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay fichas que coincidan con los filtros.
            </p>
          ) : null}
        </div>
      </div>

      <Dialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{fichasCopy.deleteDialog.title}</DialogTitle>
            <DialogDescription>
              {toDelete ? fichasCopy.deleteDialog.confirm(toDelete.vino) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setToDelete(null)}
              disabled={deleteMutation.isPending}
            >
              {fichasCopy.actions.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (toDelete) deleteMutation.mutate(toDelete.id)
              }}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {fichasCopy.actions.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={toPublish !== null}
        onOpenChange={(open) => {
          if (!open) setToPublish(null)
        }}
      >
        <DialogContent>
          {(() => {
            // Direccion segun el estado actual: borrador -> publicar; publicado -> despublicar.
            const willPublish = toPublish?.status !== 'PUBLISHED'
            return (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {willPublish
                      ? fichasCopy.publishDialog.publishTitle
                      : fichasCopy.publishDialog.unpublishTitle}
                  </DialogTitle>
                  <DialogDescription>
                    {toPublish
                      ? willPublish
                        ? fichasCopy.publishDialog.publishConfirm(toPublish.vino)
                        : fichasCopy.publishDialog.unpublishConfirm(toPublish.vino)
                      : null}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setToPublish(null)}
                    disabled={publishMutation.isPending}
                  >
                    {fichasCopy.actions.cancel}
                  </Button>
                  <Button
                    disabled={publishMutation.isPending}
                    onClick={() => {
                      if (toPublish) {
                        publishMutation.mutate({ id: toPublish.id, publish: willPublish })
                      }
                    }}
                  >
                    {publishMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {willPublish ? fichasCopy.actions.publish : fichasCopy.actions.unpublish}
                  </Button>
                </DialogFooter>
              </>
            )
          })()}
        </DialogContent>
      </Dialog>
    </div>
  )
}
